import axios from "axios";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

// Base API functions
const fetchExerciseSetsApi = async (exerciseId) => {
  const response = await axios.get(
    `/exercises/${exerciseId}/exercise_sets.json`
  );
  return response.data;
};

const fetchExerciseTypeApi = async (exerciseId) => {
  const response = await axios.get(
    `/exercises/${exerciseId}/exercise_type.json`
  );
  return response.data;
};

const createExerciseSetApi = async ({ exerciseId, reps, weight, duration }) => {
  const csrfToken = document.querySelector('meta[name="csrf-token"]').content;
  const response = await axios.post(
    `/exercises/${exerciseId}/exercise_sets.json`,
    { exercise_set: { reps, weight, duration } },
    {
      headers: {
        "X-CSRF-Token": csrfToken,
        "Content-Type": "application/json",
      },
    }
  );
  return response.data;
};

const updateExerciseSetApi = async ({
  exerciseId,
  setId,
  reps,
  weight,
  duration,
}) => {
  const csrfToken = document.querySelector('meta[name="csrf-token"]').content;
  const response = await axios.patch(
    `/exercises/${exerciseId}/exercise_sets/${setId}.json`,
    { exercise_set: { reps, weight, duration } },
    {
      headers: {
        "X-CSRF-Token": csrfToken,
        "Content-Type": "application/json",
      },
    }
  );
  return response.data;
};

const deleteExerciseSetApi = async ({ exerciseId, setId }) => {
  const csrfToken = document.querySelector('meta[name="csrf-token"]').content;
  await axios.delete(`/exercises/${exerciseId}/exercise_sets/${setId}.json`, {
    headers: {
      "X-CSRF-Token": csrfToken,
    },
  });
  return { success: true };
};

const updateExerciseSetPositionApi = async ({ setId, newPosition }) => {
  const csrfToken = document.querySelector('meta[name="csrf-token"]').content;
  const response = await axios.patch(
    "/exercise_sets/update_position",
    { exercise_set: { id: setId, position: newPosition } },
    {
      headers: {
        "X-CSRF-Token": csrfToken,
        "Content-Type": "application/json",
      },
    }
  );
  return response.data;
};

// React Query hooks
export function useExerciseSets(exerciseId) {
  return useQuery({
    queryKey: ["exerciseSets", exerciseId],
    queryFn: () => fetchExerciseSetsApi(exerciseId),
    enabled: !!exerciseId,
  });
}

export function useExerciseType(exerciseId) {
  return useQuery({
    queryKey: ["exerciseType", exerciseId],
    queryFn: () => fetchExerciseTypeApi(exerciseId),
    enabled: !!exerciseId,
  });
}

export function useCreateExerciseSet() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createExerciseSetApi,
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["exerciseSets", variables.exerciseId],
      });
    },
  });
}

export function useUpdateExerciseSet() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateExerciseSetApi,
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["exerciseSets", variables.exerciseId],
      });
    },
  });
}

export function useDeleteExerciseSet() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteExerciseSetApi,
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["exerciseSets", variables.exerciseId],
      });
    },
  });
}

export function useUpdateExerciseSetPosition() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateExerciseSetPositionApi,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["exerciseSets"] });
    },
  });
}
